import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard
} from './portalShared'
import SubcontractorTerritoryMapEditor from './SubcontractorTerritoryMapEditor'

const useStyles = makeStyles((theme) => ({
  infoCard: {
    borderRadius: 'var(--df-radius-md)',
    marginBottom: theme.spacing(3),
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  cardTitle: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  helperText: {
    color: 'var(--df-ink-3)'
  },
  adminChip: {
    backgroundColor: 'var(--df-ok-bg)',
    color: 'var(--df-ok-text)',
    border: '1px solid var(--df-ok-text)'
  },
  draftChip: {
    backgroundColor: 'var(--df-info-bg)',
    color: 'var(--df-info-text)',
    border: '1px solid var(--df-info-text)'
  }
}))

const SubcontractorTerritories = ({ user }) => {
  const classes = useStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [crews, setCrews] = useState([])
  const [summary, setSummary] = useState({})

  const loadTerritories = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/territories')
      setItems(response.data?.items || [])
      setCrews(response.data?.crews || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor territories', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor territories right now.')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    loadTerritories()
  }, [loadTerritories])

  const saveTerritories = useCallback(async (territories) => {
    setSaving(true)
    setError('')

    try {
      const response = await axios.post('/api/subcontractor/territories', {
        territories
      })
      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
      return response.data
    } catch (requestError) {
      console.error('Failed to save subcontractor territories', requestError)
      const message = requestError?.response?.data?.message || 'Unable to save subcontractor territories right now.'
      setError(message)
      throw new Error(message)
    } finally {
      setSaving(false)
    }
  }, [])

  const summaryCards = useMemo(() => ([
    {
      eyebrow: 'Territories',
      value: summary.total || items.length || 0,
      caption: 'Total territories you\'ve defined.'
    },
    {
      eyebrow: 'Assigned',
      value: summary.assigned || items.filter(item => item.assignedCrew).length,
      caption: 'Territories assigned to a crew.'
    },
    {
      eyebrow: 'Crews',
      value: crews.length,
      caption: 'Crews available for territory assignment.'
    },
    {
      eyebrow: 'Unassigned',
      value: Math.max((summary.total || items.length || 0) - (summary.assigned || items.filter(item => item.assignedCrew).length), 0),
      caption: 'Territories not yet assigned to a crew.'
    }
  ]), [crews.length, items, summary])

  if (!isAdmin) {
    return (
      <PortalStateCard
        title="Territory management is admin-only"
        message="Only admins can create or edit territories. Applicators can view their assigned areas."
        tone="muted"
        minHeight={240}
      />
    )
  }

  if (loading && !items.length && !crews.length) {
    return <PortalLoadingCard title="Loading territories" message="Loading your territories..." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={classes.infoCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="h6" className={classes.cardTitle}>
                  Your Territories
                </Typography>
                <Typography variant="body2" className={classes.helperText}>
                  Draw or edit territory boundaries on the map, then assign each one to a crew. Changes are saved when you click Save.
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label="Admin managed" className={classes.adminChip} />
                <Chip label="Crew assigned" className={classes.draftChip} />
                <Chip label={saving ? 'Saving...' : 'Map editor ready'} className={saving ? classes.draftChip : classes.adminChip} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error && !items.length ? (
        <PortalStateCard
          title="Territories unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadTerritories}
          tone="danger"
          minHeight={240}
        />
      ) : (
        <SubcontractorTerritoryMapEditor
          items={items}
          crews={crews}
          loading={loading}
          saving={saving}
          onSave={saveTerritories}
          onRefresh={loadTerritories}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorTerritories)
